import { useEffect, useRef, useState } from 'react'
import { DonorMascot } from './DonorMascot'
import MascotGame from './game/MascotGame'
import { fetchMascotGame } from './game/mascot-game-api'
import type { MascotGamePayload } from './game/mascot-game-types'
import { projectNeeds } from './game/mascot-game-view'
import { parseMascotConfig, type MascotConfig } from './mascot-catalog'
import {
  campaignMascotBackdrop,
  isSheepAvailable,
  withMascotBackdrop,
} from './mascot-campaign'
import { parseAvatarConfig } from '../donorAvatar/avatar-catalog'
import styles from './Mascot.module.css'

export function ProfileMascot({
  mascot,
  avatar,
  isProfileComplete,
  onCompleteProfile,
  onSaved,
  onGameClosed,
  onVeterinaryPaid,
  campaignId,
}: {
  mascot?: unknown
  avatar?: unknown
  isProfileComplete: boolean
  onCompleteProfile: () => void
  onSaved: (config: MascotConfig) => void
  onGameClosed?: () => void
  onVeterinaryPaid?: () => void
  campaignId: number
}) {
  const parsedConfig = parseMascotConfig(mascot)
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [preview, setPreview] = useState<{
    species: MascotConfig['species']
    data: MascotGamePayload
  } | null>(null)
  const [now, setNow] = useState(() => Date.now() / 1000)
  const clockOffset = useRef(0)
  const messageTimeout = useRef<number | null>(null)
  const species = parsedConfig?.species
  const currentPreview =
    preview?.data.campaign_id === campaignId && preview.species === species
      ? preview.data
      : null
  const requiredBackdrop = campaignMascotBackdrop(
    campaignId,
    currentPreview?.catalog.required_backdrop
  )
  const config = parsedConfig ? withMascotBackdrop(parsedConfig, requiredBackdrop) : null
  const restrictedSheep =
    species === 'sheep' && !isSheepAvailable(currentPreview?.catalog)

  useEffect(() => {
    if (!isProfileComplete || !species || open) return
    let controller: AbortController | null = null
    const refresh = () => {
      controller?.abort()
      controller = new AbortController()
      const signal = controller.signal
      void fetchMascotGame(campaignId, signal)
        .then((payload) => {
          if (signal.aborted || payload.campaign_id !== campaignId) return
          clockOffset.current = payload.server_time - Date.now() / 1000
          setPreview({ species, data: payload })
          setNow(payload.server_time)
        })
        .catch(() => {
          // Keep the last known appearance if a background refresh fails.
        })
    }
    refresh()
    const onVisible = () => {
      if (!document.hidden) refresh()
    }
    document.addEventListener('visibilitychange', onVisible)
    const timer = window.setInterval(
      () => setNow(Date.now() / 1000 + clockOffset.current),
      30000
    )
    return () => {
      controller?.abort()
      document.removeEventListener('visibilitychange', onVisible)
      window.clearInterval(timer)
    }
  }, [campaignId, species, isProfileComplete, open])

  useEffect(
    () => () => {
      if (messageTimeout.current !== null) window.clearTimeout(messageTimeout.current)
    },
    []
  )

  return (
    <div className={styles.profile}>
      <button
        type="button"
        className={styles.trigger}
        aria-haspopup={isProfileComplete ? 'dialog' : undefined}
        aria-label={
          !isProfileComplete
            ? 'Mascote bloqueado: complete seu perfil'
            : restrictedSheep
              ? 'Ver mascotes desta campanha'
              : config
                ? 'Cuidar do meu mascote'
                : 'Adotar meu mascote'
        }
        onClick={() => {
          if (messageTimeout.current !== null) window.clearTimeout(messageTimeout.current)
          setMessage('')
          if (isProfileComplete) setOpen(true)
          else {
            setMessage('Complete e salve seu perfil para liberar o mascote.')
            messageTimeout.current = window.setTimeout(() => {
              setMessage('')
              messageTimeout.current = null
            }, 5000)
          }
        }}
      >
        <span className={styles.picture}>
          {config && !restrictedSheep ? (
            <DonorMascot
              config={config}
              needs={currentPreview ? projectNeeds(currentPreview.state, now) : undefined}
              expression={currentPreview?.state.sleeping ? 'sleepy' : 'happy'}
            />
          ) : (
            <span className={styles.placeholder} aria-hidden="true">
              {isProfileComplete ? '🐾' : '🔒'}
              <small>
                {restrictedSheep
                  ? 'Exclusiva Agroplay'
                  : isProfileComplete
                    ? 'Seu companheiro'
                    : 'Complete seu perfil'}
              </small>
            </span>
          )}
          <span className={styles.edit} aria-hidden="true">
            {config ? '♡' : '+'}
          </span>
        </span>
        <span className={styles.triggerLabel}>
          {config?.name ? (
            <strong className={styles.triggerName} title={config.name}>
              {config.name}
            </strong>
          ) : null}
          <span>
            {!isProfileComplete
              ? 'Mascote bloqueado'
              : restrictedSheep
                ? 'Ver mascotes desta campanha'
                : config
                  ? 'Cuidar e brincar'
                  : 'Adotar mascote'}
          </span>
        </span>
      </button>
      {message && !isProfileComplete && (
        <div role="status" className={styles.feedback}>
          {message}
          <button
            type="button"
            onClick={() => {
              if (messageTimeout.current !== null)
                window.clearTimeout(messageTimeout.current)
              messageTimeout.current = null
              setMessage('')
              onCompleteProfile()
            }}
          >
            Completar cadastro
          </button>
        </div>
      )}
      {open && isProfileComplete && (
        <MascotGame
          key={campaignId}
          campaignId={campaignId}
          mascot={config}
          avatar={parseAvatarConfig(avatar)}
          onClose={() => {
            setOpen(false)
            onGameClosed?.()
          }}
          onSaved={onSaved}
          onVeterinaryPaid={onVeterinaryPaid}
        />
      )}
    </div>
  )
}
